import { useProductsBySlug } from '@gik/api/products/productsBySlug';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/CustomHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import type { Product } from '@gik/core/models/gik/Product';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import type { TaxonomyPageServerProps } from '@gik/shop/pages/TaxonomyPage/TaxonomyPage';
import type { GiveTaxonomyType } from '@gik/shop/types/GiveTaxonomyType';
import { Button } from '@gik/ui-legacy/Button';
import { SkeletonNetworkError, SkeletonNetworkRetryMessage } from '@gik/core/components/NetworkError/NetworkError';
import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';
import React from 'react';
import { ProductsGrid, ProductsGridSkeleton } from '../products/ProductsGrid/ProductsGrid';

export function getTaxonomySlugParam(taxonomyType: GiveTaxonomyType) {
  switch (taxonomyType) {
    case 'situation':
      return 'situationSlugs';
    case 'recipient':
      return 'recipientSlugs';
    case 'category':
      return 'productCategorySlugs';
  }
}

export type ProductsGridSectionProps = {
  taxonomyType?: GiveTaxonomyType;
  parentSlug?: string;
  childSlug?: string;
  currentSlug?: string;
  title?: string;
  fallbackData?: Partial<TaxonomyPageServerProps>;
  initialPage?: number;
  perPage?: number;
  emphasis?: {
    title: string;
    node: React.ReactNode;
  };
} & UIComponent;

function ProductsGridSectionComp({
  taxonomyType,
  parentSlug,
  childSlug,
  currentSlug,
  fallbackData,
  initialPage = 1,
  perPage = 12,
  emphasis,
}: ProductsGridSectionProps): React.ReactElement {
  const bem = useBemCN('products-grid-section');

  const isMounted = useIsMounted();
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);

  const [_initialData, setInitialData] = React.useState<Product[]>(fallbackData?.products ?? []);

  const [page, setPage] = React.useState<number>(initialPage);
  const [allProducts, setAllProducts] = React.useState<Product[]>(fallbackData?.products ?? []);
  const [totalPages, setTotalPages] = React.useState<number>(fallbackData?.productsTotalPages ?? 0);
  const [_perPage] = React.useState<number>(perPage);

  const slug = currentSlug || childSlug || parentSlug;
  const slugParam = getTaxonomySlugParam(taxonomyType);
  const requestKey = `products-grid-${taxonomyType}-${slug}-page-${page}`;

  // prettier-ignore
  const {
		data: products,
		headers,
		isValidating,
		mutate,
	} = useProductsBySlug(
		{ [slugParam]: [slug], perPage: _perPage, page },
		{ 
			fallbackData: currentSlug ? null : fallbackData?.products,
			requestKey,
		},
	);

  React.useEffect(() => {
    if (!isValidating && products) {
      // TODO: find a better way to only concat new products

      const newProducts = [];

      products.forEach(product => {
        if (!allProducts?.find(p => p.id === product.id)) {
          newProducts.push(product);
        }
      });

      setAllProducts(allProducts?.concat(newProducts));

      if (headers) setTotalPages(headers?.['x-total-pages']);
    }
    // eslint-disable-next-line
  }, [isValidating]);

  function handleGotoPage(num: number) {
    setInitialData(undefined);
    setPage(num);
  }

  function handleLoadMore() {
    handleGotoPage(page + 1);
  }

  return (
    <div {...bem()}>
      <section {...bem('title-section')}>
        {isMounted() && !isMdDown && emphasis?.title && <span {...bem('title-carousel')}>{emphasis.title}</span>}
        {taxonomyType === 'situation' && <span {...bem('title')}>Thoughtful Gift Ideas</span>}
      </section>

      {/* Show skeleton during initial load or if NetworkStore indicates error */}
      {(!allProducts || allProducts.length === 0) && !NetworkStoreApi.isSuccess(requestKey) ? (
        <div className="relative">
          <ProductsGridSkeleton cols={isMdDown ? 2 : 4} skeletonItems={_perPage} />
          {NetworkStoreApi.shouldShowRetryMessage(requestKey) && !NetworkStoreApi.isError(requestKey) && (
            <SkeletonNetworkRetryMessage />
          )}
          {NetworkStoreApi.isError(requestKey) && (
            <SkeletonNetworkError
              onReload={() => {
                NetworkStoreApi.reload(requestKey);
                mutate();
              }}
            />
          )}
        </div>
      ) : (
        <>
          <ProductsGrid products={allProducts} emphasisTile={isMounted() && !isMdDown && emphasis?.node} />
          {/* <Pagination totalPages={headers?.['x-total-pages']} perPage={_perPage} page={page} onGotoPage={handleGotoPage} /> */}

          {totalPages > page && (
            <div {...bem('actions')}>
              <Button variant="default" loading={isValidating} onClick={handleLoadMore} pill>
                Load More ({page}/{totalPages})
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export const ProductsGridSection = withComponentErrorBoundary(ProductsGridSectionComp);
